import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Instagram, Facebook, MapPin, Phone, Mail, Heart, Flower2, ArrowRight, Video } from 'lucide-react';

const Footer = () => {
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  if (location.pathname.startsWith('/admin-panel')) return null;

  const getPanelRoute = () => {
    if (!user) return '/login';
    if (['Dueño', 'Admin', 'Administrador', 'Florista', 'Vendedor', 'Logistica'].includes(user.rol)) {
      return '/admin-panel/dashboard';
    }
    return '/mi-cuenta';
  };

  return (
    <footer className="bg-[#0f172a] text-slate-300 pt-16 pb-8 relative overflow-hidden mt-auto border-t border-slate-800 font-sans print:hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-orange-500/5 rounded-full blur-[100px] pointer-events-none translate-x-1/3 -translate-y-1/3" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-pink-500/5 rounded-full blur-[90px] pointer-events-none -translate-x-1/3 translate-y-1/3" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 mb-12 border-b border-slate-800/60 pb-10">
          
          <div className="lg:col-span-4 space-y-5">
            <Link to="/" className="flex items-center gap-3 group w-fit">
              <div className="bg-gradient-to-br from-white to-slate-200 p-2 rounded-xl shadow-lg group-hover:rotate-3 transition-transform">
                <img src="/logo.png" alt="Dolce Fleur" className="w-8 h-8 object-contain" onError={(e)=>{e.target.style.display='none'; e.target.nextSibling.style.display='block'}}/>
                <Flower2 className="w-8 h-8 text-orange-500 hidden"/>
              </div>
              <span className="text-2xl font-black text-white tracking-tight">
                Dolce<span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-pink-500">Fleur</span>
              </span>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed max-w-sm">
              Alta costura floral. Diseñamos experiencias inolvidables con flores frescas y preservadas. Envíos premium garantizados.
            </p>
            <div className="flex gap-3 pt-2">
              <SocialIcon icon={Instagram} href="https://instagram.com" color="hover:bg-pink-600" />
              <SocialIcon icon={Facebook} href="https://facebook.com" color="hover:bg-blue-600" />
              <SocialIcon icon={Video} href="https://tiktok.com" color="hover:bg-black" />
            </div>
          </div>

          <div className="lg:col-span-2 lg:col-start-6">
            <h3 className="font-bold text-white text-base mb-4 flex items-center gap-2">Explorar</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to={getPanelRoute()} className="group flex items-center gap-2 text-orange-400 font-bold hover:text-orange-300 transition-colors">
                  {user ? 'Mi Panel' : 'Iniciar Sesión'} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform"/>
                </Link>
              </li>
              <FooterLink to="/catalogo" text="Catálogo" />
              <FooterLink to="/cotizar/crea-tu-ramo" text="Diseñador 3D" />
              <FooterLink to="/carrito" text="Carrito de Compras" />
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h3 className="font-bold text-white text-base mb-4">Ayuda</h3>
            <ul className="space-y-3 text-sm">
              <FooterLink to="/privacidad" text="Privacidad" />
              <FooterLink to="/terminos" text="Términos y Condiciones" />
              <FooterLink to="/faq" text="Preguntas Frecuentes" />
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h3 className="font-bold text-white text-base mb-4">Contacto</h3>
            <ul className="space-y-4 text-sm">
              <ContactItem icon={MapPin} text={<>Toluca, Metepec <br/>y Ciudad de México.</>} />
              <ContactItem icon={Phone} text="722 612 8347" href="tel:+527226128347" />
              <ContactItem icon={Mail} text="contacto@dolcefleur.com" href="mailto:contacto@dolcefleur.com" />
            </ul>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-500 font-medium">
          <p>© {new Date().getFullYear()} Dolce Fleur. Todos los derechos reservados.</p>
          <div className="flex items-center gap-1.5 bg-slate-900/50 px-3 py-1.5 rounded-full border border-slate-800">
            <span>Made with</span>
            <Heart size={10} className="text-red-500 fill-red-500 animate-pulse"/>
            <span>by <span className="text-slate-300 font-bold">Radam Tech</span></span>
          </div>
        </div>
      </div>
    </footer>
  );
};

const SocialIcon = ({ icon: Icon, href, color }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className={`w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 ${color} hover:text-white transition-all hover:-translate-y-1`}>
    <Icon size={16} strokeWidth={2} />
  </a>
);

const FooterLink = ({ to, text }) => (
  <li><Link to={to} className="text-slate-400 hover:text-white transition-colors hover:pl-1 block">{text}</Link></li>
);

const ContactItem = ({ icon: Icon, text, href }) => (
  <li className="flex items-start gap-3 group">
    <div className="p-1.5 bg-slate-800 rounded-lg text-slate-400 group-hover:text-orange-400 transition-colors mt-0.5"><Icon size={14}/></div>
    {href ? <a href={href} className="text-slate-400 hover:text-white transition-colors leading-relaxed">{text}</a> : <span className="text-slate-400 leading-relaxed">{text}</span>}
  </li>
);

export default Footer;